/**
 * Owns one {@link FrameDecoder} per asset referenced by an EDL and resolves
 * `assetId → media URL` through an injected resolver (API proxy in production,
 * a local file in standalone dev). Decoders open lazily on first use, but can be
 * {@link DecoderPool.preload}ed so playback doesn't stall on a black frame.
 *
 * Exposes per-asset load state as an external store (subscribe/getStates) so the
 * timeline can surface loading/error status on each clip.
 */
import { FrameDecoder, type DecodedFrame } from './frame-decoder';

export type AssetResolver = (assetId: string) => string | Promise<string>;

/** Load state of one asset's decoder. Absent from the map = not yet requested. */
export type AssetLoadState = 'loading' | 'ready' | 'error';

/** Per-asset load status: state + download progress (0..1, 0 if indeterminate). */
export interface AssetStatus {
  state: AssetLoadState;
  progress: number;
}

export class DecoderPool {
  private decoders = new Map<string, FrameDecoder>();
  private resolve: AssetResolver;
  private fps: number;

  private status = new Map<string, AssetStatus>();
  private lastPct = new Map<string, number>(); // throttles progress emits to whole %
  private snapshot: Record<string, AssetStatus> = {};
  private listeners = new Set<() => void>();

  constructor(resolve: AssetResolver, fps = 30) {
    this.resolve = resolve;
    this.fps = fps;
  }

  // ── Load-status external store (for React useSyncExternalStore) ──
  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };
  /** Immutable snapshot of asset → {state, progress} (new identity only on change). */
  getStates = (): Record<string, AssetStatus> => this.snapshot;

  private commit(): void {
    this.snapshot = Object.fromEntries([...this.status].map(([k, v]) => [k, { ...v }]));
    for (const cb of this.listeners) cb();
  }

  private setState(assetId: string, s: AssetLoadState): void {
    const cur = this.status.get(assetId);
    const progress = s === 'ready' ? 1 : s === 'loading' ? 0 : (cur?.progress ?? 0);
    if (cur && cur.state === s && cur.progress === progress) return;
    this.status.set(assetId, { state: s, progress });
    if (s === 'loading') this.lastPct.delete(assetId);
    this.commit();
  }

  private setProgress(assetId: string, fraction: number): void {
    const pct = Math.floor(fraction * 100);
    if (this.lastPct.get(assetId) === pct) return; // only emit on whole-% change
    this.lastPct.set(assetId, pct);
    const cur = this.status.get(assetId);
    this.status.set(assetId, { state: cur?.state ?? 'loading', progress: fraction });
    this.commit();
  }

  /**
   * 열려 있는(=진행 중인) open() 을 자산별로 하나로 묶는다.
   *
   * 🔴 이게 없으면 **같은 파일을 몇 번이고 다시 받는다.** 디코더가 준비되기 전에
   * 들어오는 `frameAt` 은 전부 `dec.ready === false` 를 보고 `dec.open()` 을 다시
   * 부르는데, `open()` 은 매번 새 fetch 다. 렌더 루프가 프레임을 계속 요청하므로
   * 요청이 겹겹이 쌓인다 — 실측 2026-09-11(프로덕션): 100MB 클립 하나를 여는 데
   * **7번 요청 · 571MB** 가 나갔다. 클립 5개가 같은 소스를 가리키는 프로젝트라
   * 체감 로딩도 그만큼 길어진다.
   */
  private opening = new Map<string, Promise<FrameDecoder>>();

  private decoderFor(assetId: string): Promise<FrameDecoder> {
    const inflight = this.opening.get(assetId);
    if (inflight) return inflight;

    let dec = this.decoders.get(assetId);
    if (!dec) {
      dec = new FrameDecoder(this.fps);
      this.decoders.set(assetId, dec);
    }
    if (dec.ready) return Promise.resolve(dec);

    const decoder = dec;
    const job = (async () => {
      if (this.status.get(assetId)?.state !== 'loading') this.setState(assetId, 'loading');
      try {
        const url = await this.resolve(assetId);
        await decoder.open(url, (p) => this.setProgress(assetId, p));
        this.setState(assetId, 'ready');
        return decoder;
      } catch (e) {
        this.setState(assetId, 'error');
        throw e;
      }
    })();
    // 성공이든 실패든 자물쇠를 푼다 — 실패한 자산은 다음 프레임 접근에서 다시 시도한다.
    this.opening.set(assetId, job);
    void job
      .catch(() => {})
      .finally(() => {
        if (this.opening.get(assetId) === job) this.opening.delete(assetId);
      });
    return job;
  }

  /**
   * Warm up decoders ahead of playback so frames are ready when the playhead
   * arrives. Fire-and-forget; failures surface as an 'error' load state (and are
   * retried on real frame access). Skips assets already loading/ready.
   */
  preload(assetIds: Iterable<string>): void {
    for (const id of new Set(assetIds)) {
      const st = this.status.get(id)?.state;
      if (st === 'ready' || st === 'loading') continue;
      void this.decoderFor(id).catch(() => {});
    }
  }

  async frameAt(assetId: string, frameIndex: number): Promise<DecodedFrame> {
    const dec = await this.decoderFor(assetId);
    return dec.frameAt(frameIndex);
  }

  /** Resolve an asset to its media URL (shared with the audio player). */
  url(assetId: string): string | Promise<string> {
    return this.resolve(assetId);
  }

  /** Natural length (fps frames) of an opened asset, for looping GIFs. 0 if not open. */
  async frameCount(assetId: string): Promise<number> {
    const dec = this.decoders.get(assetId);
    return dec ? dec.frameCount() : 0;
  }

  /** Source pixel dimensions of an already-opened asset (0 if not opened). */
  dimensions(assetId: string): { width: number; height: number } {
    const dec = this.decoders.get(assetId);
    return dec ? { width: dec.width, height: dec.height } : { width: 0, height: 0 };
  }

  dispose(): void {
    for (const dec of this.decoders.values()) dec.dispose();
    this.decoders.clear();
    this.opening.clear();
    this.listeners.clear();
  }
}
